import React, {useEffect, useState} from "react";
import {
    Table,
    Card,
    Typography,
    Input,
    Select,
    message,
    Row,
    Col,
    Tag,
} from "antd";
import api from "../../api";
import {useNavigate} from "react-router-dom";
import {SearchOutlined} from "@ant-design/icons";

const {Title} = Typography;
const {Option} = Select;

type SlotSegment = "strong" | "normal" | "none";

const slotSegmentTag = (value: string) => {
    switch (value) {
        case "strong":
            return <Tag color="red">Strong Slot</Tag>;
        case "normal":
            return <Tag color="gold">Normal Slot</Tag>;
        case "none":
            return <Tag color="default">Non-Slot</Tag>;
        default:
            return <Tag>{value}</Tag>;
    }
};

const CCCustomerPage: React.FC = () => {
    const [customers, setCustomers] = useState<any[]>([]);
    const [searchName, setSearchName] = useState("");
    const [vipStatusFilter, setVipStatusFilter] = useState<string | null>(null);
    const [sourceFilter, setSourceFilter] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [total, setTotal] = useState(0);
    const [slotSegments, setSlotSegments] = useState<SlotSegment[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const navigate = useNavigate();

    useEffect(() => {
        setPagination((p) => ({...p, current: 1}));
    }, [searchName, vipStatusFilter, sourceFilter, slotSegments]);

    useEffect(() => {
        fetchCustomers(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, searchName, vipStatusFilter, sourceFilter, slotSegments]);

    const fetchCustomers = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const res = await api.get("/cc/customers/", {
                params: {
                    page,
                    page_size: pageSize,
                    search: searchName || undefined,
                    vip_status: vipStatusFilter || undefined,
                    source: sourceFilter || undefined,
                    slot_segment: slotSegments.length ? slotSegments.join(",") : undefined,
                },
            });

            const results = res.data.results ?? [];

            setCustomers(results);
            setTotal(res.data.count);
        } catch {
            message.error("Failed to load customers");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "Name",
            render: (text: any, rec: any) => `${rec.first_name} ${rec.last_name}`,
        },
        {
            title: "MMID",
            dataIndex: "mmid",
        },
        {
            title: "Nationality",
            dataIndex: "nationality",
        },
        {
            title: "Phone",
            dataIndex: "phone",
        },
        {
            title: "VIP Status",
            dataIndex: "vip_status",
        },
        {
            title: "Source",
            dataIndex: "source",
        },
    ];

    return (
        <Card
            className="rounded-2xl shadow-sm"
            title={
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{margin: 0}}>
                            Casino Club Customers
                        </Title>
                    </Col>
                </Row>
            }
        >
            <Row gutter={[16, 16]} style={{marginBottom: 16}} wrap>
                <Col xs={24} sm={16} md={12} lg={8} xl={6}>
                    <Input
                        placeholder="Search by name or MMID"
                        prefix={<SearchOutlined/>}
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        allowClear
                        className="rounded-md"
                    />
                </Col>

                <Col xs={24} sm={8} md={6} lg={4} xl={3}>
                    <Select
                        allowClear
                        placeholder="VIP Status"
                        onChange={(val) => setVipStatusFilter(val || null)}
                        style={{width: "100%"}}
                        className="rounded-md"
                    >
                        <Option value="Regular">Regular</Option>
                        <Option value="VIP">VIP</Option>
                        <Option value="VVIP">VVIP</Option>
                        <Option value="Celebrity">Celebrity</Option>
                    </Select>
                </Col>

                <Col xs={24} sm={8} md={6} lg={4} xl={3}>
                    <Select
                        allowClear
                        placeholder="Source"
                        onChange={(val) => setSourceFilter(val || null)}
                        style={{width: "100%"}}
                        className="rounded-md"
                    >
                        <Option value="Official LINE">Official LINE</Option>
                        <Option value="Private LINE">Private LINE</Option>
                        <Option value="Telegram">Telegram</Option>
                        <Option value="Email">Email</Option>
                        <Option value="Wallet">Wallet</Option>
                        <Option value="Agent LINE">Agent LINE</Option>
                        <Option value="Poker">Poker</Option>
                        <Option value="Other">Other</Option>
                    </Select>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="Slot Segment"
                        value={slotSegments}
                        onChange={(vals) => setSlotSegments(vals as SlotSegment[])}
                        style={{width: "100%"}}
                        className="rounded-md"
                        maxTagCount="responsive"
                        tagRender={({value, closable, onClose}) => {
                            const v = String(value);

                            const color =
                                v === "strong" ? "red" :
                                    v === "normal" ? "gold" :
                                        "default";

                            const label =
                                v === "strong" ? "Strong Slot" :
                                    v === "normal" ? "Normal Slot" :
                                        "Non-Slot";

                            return (
                                <Tag
                                    color={color}
                                    closable={closable}
                                    onClose={onClose}
                                    style={{marginRight: 3}}
                                >
                                    {label}
                                </Tag>
                            );
                        }}
                    >
                        <Option value="strong">Strong Slot Players</Option>
                        <Option value="normal">Normal Slot Players</Option>
                        <Option value="none">Non-Slot Players</Option>
                    </Select>
                </Col>
            </Row>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={customers}
                columns={columns}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                scroll={{x: true}}
                onRow={(record) => ({
                    onClick: () => navigate(`/cc/customers/${record.id}`),
                    style: {
                        cursor: "pointer",
                        transition: "background-color 0.2s ease",
                    },
                })}
                className="rounded-xl overflow-hidden"
            />
        </Card>
    );
};

export default CCCustomerPage;
